// Phase 2 Week 2: ロール変更確認ダイアログ
// 設計決定: Option B（詳細確認）- 権限変更内容を詳細表示

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  IconButton,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  SwapHoriz as SwapHorizIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { CompanyEmployeeRole } from '../../types/employee';
import employeeService from '../../services/employeeService';

interface RoleChangeConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  employeeName: string;
  currentRole: CompanyEmployeeRole;
  newRole: CompanyEmployeeRole;
  isLoading: boolean;
}

// ロール別の権限リスト
const getRolePermissions = (role: CompanyEmployeeRole): string[] => {
  switch (role) {
    case CompanyEmployeeRole.COMPANY_ADMIN:
      return [
        '従業員の追加・編集・削除',
        'ロール変更（全従業員対象）',
        'ステータス変更（全従業員対象）',
        'スカウト送信',
        'メッセージ管理',
        '企業情報編集',
        '削除時は全従業員が連動削除',
      ];
    case CompanyEmployeeRole.HR:
      return [
        '従業員の追加・編集・削除',
        'ロール変更（全従業員対象）',
        'ステータス変更（全従業員対象）',
        'スカウト送信',
        'メッセージ管理',
        '企業情報編集',
        '削除時は本人のみ削除（連動なし）',
      ];
    case CompanyEmployeeRole.EMPLOYEE:
      return ['自分の情報の閲覧・編集のみ'];
    default:
      return [];
  }
};

export const RoleChangeConfirmDialog: React.FC<
  RoleChangeConfirmDialogProps
> = ({
  open,
  onClose,
  onConfirm,
  employeeName,
  currentRole,
  newRole,
  isLoading,
}) => {
  const currentPermissions = getRolePermissions(currentRole);
  const newPermissions = getRolePermissions(newRole);
  const currentRoleLabel = employeeService.getRoleLabel(currentRole);
  const newRoleLabel = employeeService.getRoleLabel(newRole);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ pb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SwapHorizIcon
              sx={{ fontSize: 28, color: 'primary.main', mr: 1 }}
            />
            <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
              ロール変更の確認
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            disabled={isLoading}
            sx={{ color: 'text.secondary' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 3 }}>
        {/* 対象従業員情報 */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" sx={{ mb: 1 }}>
            <strong>対象:</strong> {employeeName}
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            <strong>現在のロール:</strong> {currentRoleLabel}
          </Typography>
          <Typography variant="body1">
            <strong>変更後のロール:</strong> {newRoleLabel}
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* 権限変更内容 */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <WarningIcon sx={{ color: 'warning.main', mr: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              権限の変更内容
            </Typography>
          </Box>

          {/* 現在の権限 */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}
            >
              現在の権限:
            </Typography>
            <Box
              component="ul"
              sx={{
                m: 0,
                pl: 3,
                listStyleType: 'disc',
              }}
            >
              {currentPermissions.map((permission, index) => (
                <Typography
                  component="li"
                  variant="body2"
                  key={index}
                  sx={{ mb: 0.5 }}
                >
                  {permission}
                </Typography>
              ))}
            </Box>
          </Box>

          {/* 変更後の権限 */}
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}
            >
              変更後の権限:
            </Typography>
            <Box
              component="ul"
              sx={{
                m: 0,
                pl: 3,
                listStyleType: 'disc',
              }}
            >
              {newPermissions.map((permission, index) => (
                <Typography
                  component="li"
                  variant="body2"
                  key={index}
                  sx={{ mb: 0.5 }}
                >
                  {permission}
                </Typography>
              ))}
            </Box>
          </Box>
        </Box>

        {/* 注意事項 */}
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
            ✅ 注意事項
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
              この変更は即座に反映されます
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
              変更後、{employeeName}の権限が変更されます
            </Typography>
            <Typography component="li" variant="body2">
              この操作は取り消すことができます（再度ロールを変更）
            </Typography>
          </Box>
        </Alert>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          color="inherit"
          disabled={isLoading}
          sx={{ minWidth: 100 }}
        >
          キャンセル
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color="primary"
          disabled={isLoading}
          startIcon={
            isLoading ? <CircularProgress size={16} color="inherit" /> : null
          }
          sx={{ minWidth: 140 }}
        >
          {isLoading ? '変更中...' : 'ロール変更を実行'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

